import { Routes, Route } from 'react-router-dom';
import { AdminAuthGuard } from './components/AdminAuthGuard';
import { AdminLayout } from './components/AdminLayout';
import { DashboardPage } from './pages/DashboardPage';
import { ModulesListPage } from './pages/modules/ModulesListPage';
import { ModuleFormPage } from './pages/modules/ModuleFormPage';
import { TenantsListPage } from './pages/tenants/TenantsListPage';
import { TenantFormPage } from './pages/tenants/TenantFormPage';
import { TenantDetailPage } from './pages/tenants/TenantDetailPage';
import { TenantModuleConfigPage } from './pages/tenants/TenantModuleConfigPage';
import { UsersListPage } from './pages/users/UsersListPage';
import { UserFormPage } from './pages/users/UserFormPage';
import { RegistriesListPage } from './pages/erp/RegistriesListPage';
import { RegistryFormPage } from './pages/erp/RegistryFormPage';
import { RegistryDetailPage } from './pages/erp/RegistryDetailPage';
import { EndpointFormPage } from './pages/erp/EndpointFormPage';
import { EndpointDryRunPage } from './pages/erp/EndpointDryRunPage';
import { ErrorMappingFormPage } from './pages/erp/ErrorMappingFormPage';
import { ErpRequestLogsListPage } from './pages/erp/ErpRequestLogsListPage';
import { ErpRequestLogDetailPage } from './pages/erp/ErpRequestLogDetailPage';
import { TraceListPage } from './pages/observability/TraceListPage';
import { TraceDetailPage } from './pages/observability/TraceDetailPage';
import { WorkflowDefinitionsListPage } from './pages/workflows/WorkflowDefinitionsListPage';
import { WorkflowDefinitionDetailPage } from './pages/workflows/WorkflowDefinitionDetailPage';
import { WorkflowExecutionsListPage } from './pages/workflows/WorkflowExecutionsListPage';
import { WorkflowExecutionDetailPage } from './pages/workflows/WorkflowExecutionDetailPage';
import { WorkflowSchedulesListPage } from './pages/workflows/WorkflowSchedulesListPage';
import { WorkflowScheduleFormPage } from './pages/workflows/WorkflowScheduleFormPage';
import { WorkflowDefinitionFormPage } from './pages/workflows/WorkflowDefinitionFormPage';
import { WorkflowStepFormPage } from './pages/workflows/WorkflowStepFormPage';
import { WorkflowRuleFormPage } from './pages/workflows/WorkflowRuleFormPage';
import { ComponentConfigPage } from './pages/component-config/ComponentConfigPage';
import { ComponentsListPage } from './pages/component-config/ComponentsListPage';
import { IntegratedComponentProvider } from '@components/integrated/shared';
import { adminApiClient } from './api/adminApiClient';
import { useAdminAuthStore } from './auth/store';

export function AdminRoutes() {
  const activeTenantId = useAdminAuthStore((s) => s.activeTenantId);

  return (
    <AdminAuthGuard>
      <IntegratedComponentProvider
        apiClient={adminApiClient}
        tenant={activeTenantId ?? ''}
        module="admin"
      >
        <AdminLayout>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/modules" element={<ModulesListPage />} />
            <Route path="/modules/create" element={<ModuleFormPage />} />
            <Route path="/modules/:slug/edit" element={<ModuleFormPage />} />
            <Route path="/tenants" element={<TenantsListPage />} />
            <Route path="/tenants/create" element={<TenantFormPage />} />
            <Route path="/tenants/:slug" element={<TenantDetailPage />} />
            <Route path="/tenants/:slug/edit" element={<TenantFormPage />} />
            <Route path="/tenants/:slug/modules/:moduleSlug" element={<TenantModuleConfigPage />} />
            <Route path="/users" element={<UsersListPage />} />
            <Route path="/users/create" element={<UserFormPage />} />
            <Route path="/users/:id/edit" element={<UserFormPage />} />
            <Route path="/erp/registries" element={<RegistriesListPage />} />
            <Route path="/erp/registries/create" element={<RegistryFormPage />} />
            <Route path="/erp/registries/:slug" element={<RegistryDetailPage />} />
            <Route path="/erp/registries/:slug/edit" element={<RegistryFormPage />} />
            <Route path="/erp/registries/:registrySlug/endpoints/create" element={<EndpointFormPage />} />
            <Route path="/erp/registries/:registrySlug/endpoints/:endpointSlug/edit" element={<EndpointFormPage />} />
            <Route path="/erp/registries/:registrySlug/endpoints/:endpointSlug/dry-run" element={<EndpointDryRunPage />} />
            <Route path="/erp/registries/:registrySlug/error-mappings/create" element={<ErrorMappingFormPage />} />
            <Route path="/erp/registries/:registrySlug/error-mappings/:mappingSlug/edit" element={<ErrorMappingFormPage />} />
            <Route path="/erp/request-logs" element={<ErpRequestLogsListPage />} />
            <Route path="/erp/request-logs/:id" element={<ErpRequestLogDetailPage />} />

            {/* Observability */}
            <Route path="/observability/traces" element={<TraceListPage />} />
            <Route path="/observability/traces/:traceId" element={<TraceDetailPage />} />

            {/* Workflow executions */}
            <Route path="/workflows/executions" element={<WorkflowExecutionsListPage />} />
            <Route path="/workflows/executions/:id" element={<WorkflowExecutionDetailPage />} />

            {/* Workflow schedules */}
            <Route path="/workflows/schedules" element={<WorkflowSchedulesListPage />} />
            <Route path="/workflows/schedules/create" element={<WorkflowScheduleFormPage />} />
            <Route path="/workflows/schedules/:id/edit" element={<WorkflowScheduleFormPage />} />

            {/* Workflow definitions - more specific routes first */}
            <Route path="/workflows/create" element={<WorkflowDefinitionFormPage />} />
            <Route path="/workflows/:slug/edit" element={<WorkflowDefinitionFormPage />} />
            <Route path="/workflows/:slug/steps/create" element={<WorkflowStepFormPage />} />
            <Route path="/workflows/:slug/steps/:stepId/edit" element={<WorkflowStepFormPage />} />
            <Route path="/workflows/:slug/rules/create" element={<WorkflowRuleFormPage />} />
            <Route path="/workflows/:slug/rules/:ruleId/edit" element={<WorkflowRuleFormPage />} />
            <Route path="/workflows" element={<WorkflowDefinitionsListPage />} />
            <Route path="/workflows/:slug" element={<WorkflowDefinitionDetailPage />} />

            {/* Configurable components inventory + Phase 5 config page */}
            <Route path="/components" element={<ComponentsListPage />} />
            <Route path="/components/:componentId/config" element={<ComponentConfigPage />} />
          </Routes>
        </AdminLayout>
      </IntegratedComponentProvider>
    </AdminAuthGuard>
  );
}
